import React, { useState, useEffect } from 'react';
import { CheckCircle, Crown, Zap, Shield, Infinity } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Layout/Navbar';
import './PremiumPage.css';

export default function PremiumPage() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await api.get('/premium/plans');
      setPlans(res.data.plans.filter(p => p.id !== 'free'));
    } catch { toast.error('Failed to load plans'); }
  };

  const handleUpgrade = async (planId) => {
    setLoading(true);
    setSelectedPlan(planId);
    try {
      const res = await api.post('/premium/create-order', { plan: planId.toLowerCase().trim() });
      const options = {
        key: res.data.key,
        amount: res.data.order.amount,
        currency: 'INR',
        name: 'Zip2Extract AI',
        description: `${res.data.plan} Plan Subscription`,
        order_id: res.data.order.id,
        handler: async (response) => {
          try {
            await api.post('/premium/verify-payment', {
              ...response,
              plan: planId
            });
            await refreshUser();
            toast.success('🎉 Subscription activated!');
          } catch { toast.error('Payment verification failed'); }
        },
        prefill: { email: user.email, name: user.name },
        theme: { color: '#00ff88' }
      };
      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.error('Payment gateway not loaded. Add Razorpay script to index.html');
      }
      
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create order');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const planColors = { basic: '#00a3ff', pro: '#7c3aed', premium: '#00ff88' };
  const planIcons = { basic: Zap, pro: Crown, premium: Infinity };

  return (
    <div className="premium-page">
      <Navbar />
      <div className="premium-body">
        <div className="premium-header">
          <div className="premium-badge"><Crown size={16} /> Upgrade Your Plan</div>
          <h1>Unlock the Full Power of<br /><span className="gradient-text">Zip2Extract AI</span></h1>
          <p>File size limits, AI requests, and advanced features — upgrade to unlock everything.</p>
        </div>

        {/* Current plan */}
        <div className="current-plan-card">
          <Shield size={18} />
          <div>
            <strong>Current Plan: {user?.plan?.toUpperCase()}</strong>
            {user?.subscriptionExpiry && (
              <span className="expiry"> · Expires {new Date(user.subscriptionExpiry).toLocaleDateString()}</span>
            )}
          </div>
        </div>

        <div className="plans-grid">
          {plans.map((plan) => {
            const Icon = planIcons[plan.id] || Zap;
            const color = planColors[plan.id] || '#00ff88';
            const isCurrent = user?.plan === plan.id;

            return (
              <div key={plan.id} className={`plan-card ${plan.id === 'premium' ? 'popular' : ''} ${isCurrent ? 'current' : ''}`}
                style={{ '--plan-color': color }}>
                {plan.id === 'premium' && <div className="popular-tag">⭐ Most Popular</div>}
                {isCurrent && <div className="current-tag">Your Plan</div>}

                <div className="plan-icon" style={{ background: color + '15', border: `1px solid ${color}30`, color }}>
                  <Icon size={22} />
                </div>
                <h2 style={{ color }}>{plan.name}</h2>
                <div className="plan-price">
                  <span className="price-currency">₹</span>
                  <span className="price-amount">{plan.price}</span>
                  <span className="price-period">/month</span>
                </div>

                <ul className="plan-features">
                  {plan.features.map((f, i) => (
                    <li key={i}><CheckCircle size={14} style={{ color }} />{f}</li>
                  ))}
                </ul>

                <button
                  className="btn plan-btn"
                  style={{ background: isCurrent ? 'var(--bg-elevated)' : color, color: isCurrent ? 'var(--text-muted)' : plan.id === 'premium' ? '#000' : '#fff' }}
                  disabled={isCurrent || (loading && selectedPlan === plan.id)}
                  onClick={() => !isCurrent && handleUpgrade(plan.id)}
                >
                  {isCurrent ? 'Current Plan' : loading && selectedPlan === plan.id ? 'Processing...' : `Upgrade to ${plan.name}`}
                </button>
              </div>
            );
          })}
        </div>

        <p className="premium-note">
          💳 Payments are processed securely via Razorpay. Subscriptions are billed monthly and can be cancelled anytime.
        </p>
      </div>
    </div>
  );
}
